<template>
  <div class="flex-1 min-h-0 flex flex-col">
    <ComponentHeader title="Kairon Intelligence" :heading-level="4">
      <button
        v-if="isSuperUser"
        type="button"
        class="px-3 py-2 rounded text-[13px] font-medium text-gray-600 hover:bg-growth-25 hover:text-gray-900 transition-colors disabled:cursor-not-allowed"
        :disabled="submitting"
        title="Superuser: queue this week's engagement report"
        @click="submitReport"
      >
        Generate weekly report
      </button>
    </ComponentHeader>

    <div class="flex-1 min-h-0 flex flex-col gap-5 p-5">
      <div class="shrink-0">
        <AnalystQueryForm
          ref="queryForm"
          :loading="submitting"
          @submit="handleSubmit"
        />
        <div v-if="errorMessage" class="mt-4 px-4 py-2 bg-red-tint border border-red-light rounded">
          <p class="text-[13px] text-red-dark">{{ errorMessage }}</p>
        </div>
      </div>

      <div class="flex-1 min-h-0 flex flex-col">
        <div class="shrink-0 flex items-center justify-between gap-4 mb-2">
          <h3 class="text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">Recent</h3>
          <div v-if="isSuperUser" class="inline-flex rounded border border-gray-200 overflow-hidden text-[11px] font-medium">
            <button
              v-for="option in scopeOptions"
              :key="option.value"
              type="button"
              class="px-2 py-1 border-l border-gray-200 first:border-l-0 transition-colors"
              :class="scope === option.value ? 'bg-growth-25 text-gray-900' : 'bg-white text-gray-600 hover:text-gray-900'"
              @click="scope = option.value"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
        <AnalystHistory
          class="flex-1 min-h-0"
          :jobs="historyJobs"
          :loading="loadingJobs"
          :active-job-id="activeJob?.id"
          :show-user="scope === 'everyone'"
          @select="handleSelectJob"
        />
      </div>
    </div>

    <AnalystResultModal
      v-if="showResultModal && activeJob"
      :job="activeJob"
      :polling="polling"
      :regenerating="regenerating"
      @follow-up="handleFollowUp"
      @feedback-changed="handleFeedbackChanged"
      @regenerate="handleRegenerate"
      @close="showResultModal = false"
    />
  </div>
</template>

<script>
import AnalystQueryForm from './AnalystQueryForm.vue'
import AnalystHistory from './AnalystHistory.vue'
import AnalystResultModal from './AnalystResultModal.vue'
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue'

const PANEL_JOB_TYPES = ['personal_analyst', 'weekly_report']

export default {
  name: 'AnalystPanel',
  components: {
    AnalystQueryForm,
    AnalystHistory,
    AnalystResultModal,
    ComponentHeader,
  },
  data() {
    return {
      jobs: [],
      activeJob: null,
      loadingJobs: false,
      submitting: false,
      polling: false,
      pollInterval: null,
      errorMessage: null,
      showResultModal: false,
      regenerating: false,
      scope: 'mine',
      scopeOptions: [
        { value: 'mine', label: 'Mine' },
        { value: 'everyone', label: 'Everyone' },
      ],
    }
  },
  computed: {
    isSuperUser() {
      return this.$store.getters['auth/current_user']?.is_superuser || false
    },
    currentUserId() {
      return this.$store.getters['auth/current_user']?.id
    },
    manageJobs() {
      return this.jobs.filter(j => j.user === this.currentUserId || j.job_type === 'weekly_report')
    },
    historyJobs() {
      const source = this.scope === 'everyone' ? this.jobs : this.manageJobs
      const seen = new Set()
      return source.filter(job => {
        const key = `${job.user}|${job.job_type}|${job.query.trim().toLowerCase()}`
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
    },
  },
  mounted() {
    this.fetchJobs()
  },
  beforeUnmount() {
    this.stopPolling()
  },
  methods: {
    async fetchJobs() {
      this.loadingJobs = true
      try {
        const responses = await Promise.all(
          PANEL_JOB_TYPES.map(jobType => this.$axios.get('/api/personal-analyst/jobs/', {
            params: { entity__isnull: true, job_type: jobType },
          }))
        )
        this.jobs = responses
          .flatMap(response => response.data)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      } catch (err) {
        this.errorMessage = 'Failed to load query history.'
        return
      } finally {
        this.loadingJobs = false
      }
      await this.syncPendingJobs()
    },

    async syncPendingJobs() {
      if (!this.jobs.some(j => j.status === 'QUEUED' || j.status === 'PROCESSING')) return

      try {
        const response = await this.$axios.post('/api/personal-analyst/jobs/bulk-sync/', null, {
          params: { entity__isnull: true },
        })
        this.mergeUpdatedJobs(response.data)
      } catch {
        // Sync failure is non-fatal — list still rendered, polling will retry.
      }

      // If any are still pending after sync, start polling
      const stillPending = this.jobs.some(j => j.status === 'QUEUED' || j.status === 'PROCESSING')
      if (stillPending) {
        if (!this.activeJob) this.activeJob = this.jobs.find(j => j.status === 'QUEUED' || j.status === 'PROCESSING')
        this.startPolling()
      }
    },

    mergeUpdatedJobs(updated) {
      for (const job of updated) {
        const idx = this.jobs.findIndex(j => j.id === job.id)
        if (idx !== -1) this.jobs.splice(idx, 1, job)
        if (this.activeJob?.id === job.id) this.activeJob = job
      }
    },

    async selectJob(job) {
      this.errorMessage = null
      this.activeJob = job

      // Immediately fetch latest state via retrieve endpoint (triggers lazy sync)
      try {
        const response = await this.$axios.get(`/api/personal-analyst/jobs/${job.id}/`)
        const updatedJob = response.data
        this.activeJob = updatedJob

        const idx = this.jobs.findIndex(j => j.id === updatedJob.id)
        if (idx !== -1) {
          this.jobs.splice(idx, 1, updatedJob)
        }

        if (updatedJob.status === 'QUEUED' || updatedJob.status === 'PROCESSING') {
          this.startPolling()
        } else {
          this.stopPolling()
        }
      } catch {
        // Fall back to list data if retrieve fails
        if (job.status === 'QUEUED' || job.status === 'PROCESSING') {
          this.startPolling()
        } else {
          this.stopPolling()
        }
      }
    },

    async handleSubmit(formData) {
      this.submitting = true
      this.errorMessage = null
      try {
        const payload = { query: formData.query }
        if (formData.job_type) {
          payload.job_type = formData.job_type
        }
        const response = await this.$axios.post('/api/personal-analyst/jobs/', payload)
        const newJob = response.data
        this.jobs.unshift(newJob)
        this.activeJob = newJob
        this.startPolling()
      } catch (err) {
        if (err.response?.status === 429) {
          this.errorMessage = err.response.data.detail
        } else {
          this.errorMessage = 'Failed to submit query. Please try again.'
        }
      } finally {
        this.submitting = false
      }
    },

    handleSelectJob(job) {
      this.selectJob(job)
      this.showResultModal = true
    },

    submitReport() {
      this.handleSubmit({
        query: 'Generate weekly engagement report',
        job_type: 'weekly_report',
      })
    },

    handleFollowUp(suggestion) {
      this.$refs.queryForm.appendQuery(suggestion)
    },

    handleFeedbackChanged(feedbackItems) {
      if (this.activeJob) {
        this.activeJob = { ...this.activeJob, feedback: feedbackItems }
        const idx = this.jobs.findIndex(j => j.id === this.activeJob.id)
        if (idx !== -1) {
          this.jobs.splice(idx, 1, this.activeJob)
        }
      }
    },

    async handleRegenerate() {
      if (!this.activeJob || this.regenerating) return
      this.regenerating = true
      try {
        const response = await this.$axios.post('/api/personal-analyst/jobs/', {
          job_type: this.activeJob.job_type,
          entity: this.activeJob.entity,
        })
        const newJob = response.data
        this.jobs.unshift(newJob)
        this.activeJob = newJob
        this.startPolling()
      } catch (err) {
        if (err.response?.status === 429) {
          this.errorMessage = err.response.data.detail
        } else {
          this.errorMessage = 'Failed to regenerate. Please try again.'
        }
      } finally {
        this.regenerating = false
      }
    },

    startPolling() {
      this.stopPolling()
      this.polling = true
      this.pollInterval = setInterval(() => this.pollActiveJob(), 30000)
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval)
        this.pollInterval = null
      }
      this.polling = false
    },

    async pollActiveJob() {
      if (!this.jobs.some(j => j.status === 'QUEUED' || j.status === 'PROCESSING')) {
        this.stopPolling()
        return
      }

      try {
        const response = await this.$axios.post('/api/personal-analyst/jobs/bulk-sync/', null, {
          params: { entity__isnull: true },
        })
        this.mergeUpdatedJobs(response.data)
      } catch {
        // Non-fatal — try again next tick.
      }

      // Stop polling if all done
      if (!this.jobs.some(j => j.status === 'QUEUED' || j.status === 'PROCESSING')) {
        this.stopPolling()
      }
    },
  },
}
</script>
